import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createSalesRep,
  getSalesRepById,
  updateSalesRep,
  getUsersSalesRepsNotAssigned,
  getSalesReps,
  getSalesRepLogos,
  createSalesRepLogo,
  updateSalesRepLogo,
  logError
} from '../api';
import { Button, Form, Spinner } from 'react-bootstrap';
import EditCancelModal from './Global/EditCancelModal';

let embeddedSalesRepDraft = null;

const SalesRepForm = ({ embedded = false, onSave, onCancel }) => {
  const [salesRep, setSalesRep] = useState(() => {
    if (embedded && embeddedSalesRepDraft) return embeddedSalesRepDraft;
    return {
      name: '',
      lastName1: '',
      lastName2: '',
      email: '',
      phone: '',
      telegramName: '',
      abbrev: '',
      colourHex: '#000000',
      userId: null,
      user: null,
      managerId: null,
      manager: null,
      subordinates: [],
      updatedSubordinatesIds: [],
      commissionPercentage: 0.05,
      useCustomLogo: false,
      webPage: '',
      customerServicePhone: '',
    };
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [usersNotAssigned, setUsersNotAssigned] = useState([]);
  const [isUserAssigned, setIsUserAssigned] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [abbrevError, setAbbrevError] = useState('');
  const [allSalesReps, setAllSalesReps] = useState([]);
  const [availableSubordinates, setAvailableSubordinates] = useState([]);
  const [isSalesRepManager, setIsSalesRepManager] = useState(false);
  const [logoImage, setLogoImage] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (embedded) embeddedSalesRepDraft = salesRep;
  }, [salesRep, embedded]);


  useEffect(() => {
    if (!embedded) {
      if (id) {
        setIsEditing(true);
        getSalesRepById(id)
          .then(async data => {
            setSalesRep(data);
            setAbbreviation(data.name, data.lastName1);
            if (data.user && data.user.id) {
              setIsUserAssigned(true);
            } else {
              await fetchUsersNotAssigned(); // sólo si no hay usuario asignado
            }
            await fetchAllSalesReps(data);
            setIsSalesRepManager(data.user?.roles?.some(r => r.id === 6));
            if (data.useCustomLogo) {
              await loadSalesRepLogo(data.id);
            }
          })
          .catch(err => logError(err));
      } else {
        fetchUsersNotAssigned();
      }
    }
  }, [id, embedded]);

  const loadSalesRepLogo = async (salesRepId) => {
    try {
      const logoData = await getSalesRepLogos(salesRepId);
      if (logoData && logoData.imageDataBase64) {
        setLogoImage(`data:image/jpeg;base64,${logoData.imageDataBase64}`);
      }
    } catch (err) {
      logError(err);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedFormats = ['image/tga', 'image/bmp', 'image/jpeg', 'image/jpg', 'image/gif', 'image/png'];
      if (!allowedFormats.includes(file.type.toLowerCase())) {
        alert('Solo se permiten archivos TGA, BMP, JPEG, GIF y PNG');
        e.target.value = '';
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoImage(event.target.result);
      };
      reader.readAsDataURL(file);
      setIsDirty(true);
    }
  };

  const uploadLogo = async (salesRepId) => {
    if (!logoFile) return;
    
    try {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result.split(',')[1]);
        reader.readAsDataURL(logoFile);
      });
      
      const logoData = {
        salesRepId: salesRepId,
        imageDataBase64: base64,
        disabled: false
      };
      
      if (isEditing) {
        try {
          const existingLogo = await getSalesRepLogos(salesRepId);
          if (existingLogo && existingLogo.id) {
            const updateLogoData = {
              ...logoData,
              id: existingLogo.id
            };
            await updateSalesRepLogo(existingLogo.id, updateLogoData);
          } else {
            await createSalesRepLogo(logoData);
          }
        } catch (err) {
          await createSalesRepLogo(logoData);
        }
      } else {
        await createSalesRepLogo(logoData);
      }
    } catch (err) {
      logError(err);
    }
  };

  const fetchAllSalesReps = async (current) => {
    try {
      const all = await getSalesReps();
      const filtered = all.filter(sr => sr.id !== current.id);
      setAllSalesReps(all);
      setAvailableSubordinates(filtered);
    } catch (err) {
      logError(err);
    }
  };

  const fetchUsersNotAssigned = async () => {
    setLoadingUsers(true);
    try {
      const users = await getUsersSalesRepsNotAssigned();
      // Solo incluimos previousUser si no es null
      if (salesRep.user && salesRep.user.id) {
        setUsersNotAssigned([salesRep.user, ...users]);
      } else {
        setUsersNotAssigned(users);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      logError(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const setAbbreviation = (name, lastName1) => {
    if(id === undefined)
    {
      const abbrev = (name[0] || '') + (lastName1[0] || '');
      setSalesRep(prev => ({
        ...prev,
        abbrev: abbrev.toUpperCase()
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSalesRep(prev => {
      const updated = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'name' || name === 'lastName1') {
        setAbbreviation(updated.name, updated.lastName1);
      }
      return updated;
    });
    setIsDirty(true);
  };

  const handleColorChange = (e) => {
    setSalesRep(prev => ({ ...prev, colourHex: e.target.value }));
    setIsDirty(true);
  };

  const handleUserSelect = (e) => {
    const selectedUserId = parseInt(e.target.value);
    const selectedUser = usersNotAssigned.find(user => user.id === selectedUserId);
    setSalesRep(prev => ({
      ...prev,
      userId: selectedUser.id,
      user: selectedUser
    }));
    setIsUserAssigned(true);
    setIsDirty(true);
  };

  const handleRemoveAssignment = () => {
    const previousUser = salesRep.user;
    setSalesRep(prev => ({ ...prev, userId: null, user: null }));
    setIsUserAssigned(false);
    // Cuando quitamos la asignación, recargamos usuarios. previousUser existe, lo volvemos a añadir.
    fetchUsersNotAssigned();
    setIsDirty(true);
  };

  const handleSubordinateToggle = (subId) => {
    const exists = salesRep.subordinates?.some(s => s.id === subId);
    const updated = exists
      ? salesRep.subordinates.filter(s => s.id !== subId)
      : [...(salesRep.subordinates || []), allSalesReps.find(s => s.id === subId)];
    setSalesRep(prev => ({ ...prev, subordinates: updated }));
    setIsDirty(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const salesRepToSubmit = {
      ...salesRep,
      user: undefined,
      manager: undefined,
      updatedSubordinatesIds: salesRep.subordinates?.map(sub => sub.id) || [],
      subordinates: []
    };

    const handleError = (err) => {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || 'Unexpected error';

      if (status === 409) {
        setAbbrevError(message || 'Esta abreviatura ya está en uso.');
      } else {
        alert(message);
      }
    };

    if (isEditing) {
      updateSalesRep(id, salesRepToSubmit)
        .then(async () => {
          if (salesRep.useCustomLogo && logoFile) {
            await uploadLogo(id);
          }
          navigate('/salesreps');
        })
        .catch(handleError);
    } else {
      createSalesRep(salesRepToSubmit)
        .then(async (response) => {
          const newSalesRepId = response.data.id;
          if (salesRep.useCustomLogo && logoFile) {
            await uploadLogo(newSalesRepId);
          }
          navigate('/salesreps');
        })
        .catch(handleError);
    }
  };

  const handleSaveEmbedded = () => {
    if (onSave) {
      const salesRepToSave = { ...salesRep };
      onSave(salesRepToSave); // Envía la info al UserForm
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowCancelModal(true);
    } else {
      navigate('/salesreps');
    }
  };

  return (
    <div>
      {!embedded && <h2>{isEditing ? 'Editar Comercial' : 'Agregar Nuevo Comercial'}</h2>}

      <Form onSubmit={handleSubmit}>
        <Form.Group>
          <Form.Label>Nombre *</Form.Label>
          <Form.Control name="name" value={salesRep.name} onChange={handleChange} required data-testid="salesrep-name-input" />
        </Form.Group>

        <Form.Group>
          <Form.Label>Primer Apellido *</Form.Label>
          <Form.Control name="lastName1" value={salesRep.lastName1} onChange={handleChange} required data-testid="salesrep-lastname1-input" />
        </Form.Group>

        <Form.Group>
          <Form.Label>Segundo Apellido</Form.Label>
          <Form.Control name="lastName2" value={salesRep.lastName2} onChange={handleChange} data-testid="salesrep-lastname2-input" />
        </Form.Group>

        <Form.Group>
          <Form.Label>Email</Form.Label>
          <Form.Control type="email" name="email" value={salesRep.email} onChange={handleChange}  data-testid="salesrep-email-input" />
        </Form.Group>

        <Form.Group>
          <Form.Label>Teléfono</Form.Label>
          <Form.Control name="phone" value={salesRep.phone} onChange={handleChange} data-testid="salesrep-phone-input" />
        </Form.Group>

        <Form.Group>
          <Form.Label>Telegram</Form.Label>
          <Form.Control name="telegramName" value={salesRep.telegramName} onChange={handleChange} data-testid="salesrep-telegram-input" />
        </Form.Group>

        <Form.Group>
          <Form.Label>Abreviatura *</Form.Label>
          <Form.Control
            name="abbrev"
            value={salesRep.abbrev}
            onChange={(e) => {
              handleChange(e);
              if (abbrevError) setAbbrevError('');
            }}
            isInvalid={!!abbrevError}
            required
            maxLength={2}
            data-testid="salesrep-abbrev-input"
          />
          <Form.Control.Feedback type="invalid">{abbrevError}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group>
          <Form.Label>Porcentaje de Comisión (%)</Form.Label>
          <Form.Control
            type="number"
            name="commissionPercentage"
            value={(salesRep.commissionPercentage).toFixed(2)}
            onChange={e => {
              const pct = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
              setSalesRep(prev => ({ ...prev, commissionPercentage: pct }));
              setIsDirty(true);
            }}
            min={0}
            max={100}
            step={0.01}
            required
            data-testid="salesrep-commission-input"
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>Color</Form.Label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: 25,
                height: 25,
                backgroundColor: salesRep.colourHex,
                border: '1px solid black',
                borderRadius: '50%'
              }}
            />
            <Form.Control type="color" name="colourHex" value={salesRep.colourHex} onChange={handleColorChange} data-testid="salesrep-color-input" />
          </div>
        </Form.Group>

        <Form.Group>
          <Form.Check
            type="checkbox"
            name="useCustomLogo"
            label="Usar logo personalizado"
            checked={salesRep.useCustomLogo}
            onChange={handleChange}
            data-testid="salesrep-custom-logo-checkbox"
          />
        </Form.Group>

        {salesRep.useCustomLogo && (
          <>
          <Form.Group>
            <Form.Label>Página Web</Form.Label>
            <Form.Control
              name="webPage"
              value={salesRep.webPage || ''}
              onChange={handleChange}
              placeholder="https://"
              required
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Teléfono de Atención al Cliente</Form.Label>
            <Form.Control
              name="customerServicePhone"
              value={salesRep.customerServicePhone || ''}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Logo personalizado</Form.Label>
            {logoImage && (
              <div className="mb-2">
                <img 
                  src={logoImage} 
                  alt="Logo personalizado" 
                  style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                />
              </div>
            )}
            <Form.Control
              type="file"
              accept=".tga,.bmp,.jpeg,.jpg,.gif,.png"
              onChange={handleLogoUpload}
              data-testid="salesrep-logo-upload"
            />
            <Form.Text className="text-muted">
              Formatos permitidos: TGA, BMP, JPEG, GIF, PNG
            </Form.Text>
          </Form.Group>
          </>
        )}

        {isUserAssigned ? (
          <Form.Group>
            <Form.Label>Usuario Asignado</Form.Label>
            <div className="d-flex justify-content-between">
              <span>{salesRep.user?.username}</span>
              <Button variant="danger" onClick={handleRemoveAssignment}>
                Quitar Asignación
              </Button>
            </div>
          </Form.Group>
        ) : (
          !embedded && (
            <Form.Group>
              <Form.Label>Asignar Usuario</Form.Label>
              {loadingUsers ? (
                <div>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando usuarios…
                </div>
              ) : (
                <Form.Select value={salesRep.userId || ''} onChange={handleUserSelect}>
                  <option value="">Seleccionar Usuario</option>
                  {usersNotAssigned.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>
          )
        )}

        {isSalesRepManager && (
          <>
            <Form.Group className="mt-3">
              <Form.Label>Jefe de Comerciales</Form.Label>
            </Form.Group>
            <Form.Group>
              <Form.Label>Subordinados:</Form.Label>
              {availableSubordinates.map(rep => (
                <Form.Check
                  key={rep.id}
                  type="checkbox"
                  label={`${rep.name} ${rep.lastName1}`}
                  checked={salesRep.subordinates?.some(s => s.id === rep.id)}
                  onChange={() => handleSubordinateToggle(rep.id)}
                />
              ))}
            </Form.Group>
          </>
        )}

        {salesRep.manager && (
          <Form.Group className="mt-3">
            <Form.Label>Jefe actual</Form.Label>
            <div>
              {salesRep.manager.name} {salesRep.manager.lastName1}
            </div>
          </Form.Group>
        )}

        <div className="d-flex justify-content-end mt-4">
          {embedded ? (
            <div className="d-flex justify-content-between w-100">
              <Button variant="primary" onClick={handleSaveEmbedded} data-testid="salesrep-save-button">
                Guardar
              </Button>
              <Button variant="secondary" onClick={onCancel} data-testid="salesrep-cancel-button">
                Cancelar
              </Button>
            </div>
          ) : (
            <div className="d-flex justify-content-between w-100">
              <Button variant="primary" type="submit" data-testid="salesrep-submit-button">
                {isEditing ? 'Guardar Cambios' : 'Crear Comercial'}
              </Button>
              <Button variant="secondary" onClick={handleCancel} data-testid="salesrep-cancel-button">
                Cancelar
              </Button>
            </div>
          )}
        </div>
      </Form>

      {!embedded && (
        <EditCancelModal
          show={showCancelModal}
          onConfirm={() => navigate('/salesreps')}
          onCancel={() => setShowCancelModal(false)}
        />
      )}
    </div>
  );
};

export default SalesRepForm;
